import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { MobileView } from '@/components';
import { Button } from '@/components/ui';

import { formatCurrency } from '@/lib';
import { useLanguage } from '../contexts/LanguageContext';

interface PaymentResultParams {
  status: 'success' | 'canceled';
  message: string;
  // Null type when completed via inbox mark-complete or backfill (BES-8925): only the
  // signed event exists, so the option and amount are unknown. Render a generic
  // completed state with no amount.
  type: 'waiver' | 'hold' | 'none' | null;
  amount: string | null;
  currency: string | null;
  taxRate?: string;
  isFromCompleted?: boolean;
  returnPath?: string | null;
}

function getSafeReturnPath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  return value;
}

function DamageWaiverResult({ embedded = false }: { embedded?: boolean }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { t, i18n } = useTranslation('damageWaiverResult');
  const { language } = useLanguage();
  const Shell = embedded ? Fragment : MobileView;
  const inPortal = location.pathname.startsWith('/portal/');
  const homePath = inPortal ? '/portal/home' : '/';
  const damageWaiverPath = inPortal ? '/portal/home/damage-waiver' : '/damage-waiver';

  const [paymentParams, setPaymentParams] = useState<PaymentResultParams | null>(null);

  // Apply persisted language on mount
  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  // Extract payment parameters from URL or navigation state
  useEffect(() => {
    // Check if data was passed via navigation state (from completed waiver).
    // Type/amount/currency are optional: event-only completions (BES-8925) and
    // 'none' selections don't have them.
    if (location.state) {
      const state = location.state as PaymentResultParams;
      if (state.status && state.message) {
        setPaymentParams(state);
        return;
      }
    }

    const status = searchParams.get('status') as 'success' | 'canceled';
    const message = searchParams.get('message');
    const type = searchParams.get('type') as 'waiver' | 'hold' | 'none';
    const amount = searchParams.get('amount');
    const currency = searchParams.get('currency');
    const taxRate = searchParams.get('taxRate');

    if (status && message && type && amount && currency) {
      setPaymentParams({
        status,
        message,
        type,
        amount,
        currency,
        taxRate: taxRate || undefined,
        returnPath: getSafeReturnPath(searchParams.get('return')),
      });
    }
  }, [searchParams, location.state]);

  const getTypeDisplayName = (type: 'waiver' | 'hold' | 'none') => {
    if (type === 'waiver') return t('Damage Waiver');
    if (type === 'hold') return t('Security Deposit');
    return t('No Protection');
  };

  const formatAmount = (amount: string, currency: string) => {
    return formatCurrency(amount, currency);
  };

  // Invalid parameters - show loading or error state
  if (!paymentParams) {
    return (
      <Shell>
        <div className="flex items-center justify-center min-h-screen p-8">
          <div className="text-center space-y-6">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('Invalid Link')}</h2>
              <p className="text-gray-600">{t('This payment result link appears to be invalid or incomplete.')}</p>
            </div>
            <Button onClick={() => navigate(homePath)} className="w-full">
              {t('Go Home')}
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  const isSuccess = paymentParams.status === 'success';
  const backPath = paymentParams.returnPath || homePath;

  // Success state
  if (isSuccess) {
    return (
      <Shell>
        <div className="flex items-center justify-center min-h-screen p-8">
          <div className="text-center space-y-6 max-w-md">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <div className="space-y-2">
              {paymentParams.type === 'waiver' ? (
                <h2 className="text-2xl font-bold text-gray-900">{t('Thank you for your payment')}</h2>
              ) : paymentParams.type === 'hold' ? (
                <h2 className="text-2xl font-bold text-gray-900">{t('Hold Successful')}</h2>
              ) : paymentParams.type === 'none' ? (
                <h2 className="text-2xl font-bold text-gray-900">{t('Selection Confirmed')}</h2>
              ) : (
                <h2 className="text-2xl font-bold text-gray-900">{t('Already Completed')}</h2>
              )}
              <p className="text-gray-600">{paymentParams.message}</p>
            </div>

            {paymentParams.type && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('Protection Type:')}</span>
                    <span className="font-medium text-gray-900">{getTypeDisplayName(paymentParams.type)}</span>
                  </div>
                  {paymentParams.type !== 'none' &&
                    paymentParams.amount &&
                    paymentParams.amount !== '0' &&
                    paymentParams.currency && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('Amount:')}</span>
                        <span className="font-medium text-gray-900">
                          {formatAmount(paymentParams.amount, paymentParams.currency)}
                        </span>
                      </div>
                    )}
                  {paymentParams.taxRate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('Tax Rate:')}</span>
                      <span className="font-medium text-gray-900">{paymentParams.taxRate}%</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {paymentParams.type && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="text-sm text-blue-800 text-left">
                    <p className="font-medium mb-1">{t('What happens next?')}</p>
                    <p>
                      {paymentParams.type === 'waiver'
                        ? t(
                            "Your damage waiver is now active for your stay. You're covered for accidental damages up to the policy limit."
                          )
                        : paymentParams.type === 'hold'
                          ? t(
                              'Your security deposit has been authorized. The funds will be released 5 days after your checkout, provided there are no damages.'
                            )
                          : t(
                              'You have chosen to proceed without damage protection. You will be responsible for any damages that occur during your stay.'
                            )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button onClick={() => navigate(backPath)} className="w-full">
              {t('Continue')}
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  // Canceled/Failed state
  return (
    <Shell>
      <div className="flex items-center justify-center min-h-screen p-8">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">{t('Payment Canceled')}</h2>
            <p className="text-gray-600">{paymentParams.message}</p>
          </div>

          {paymentParams.type && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('Protection Type:')}</span>
                  <span className="font-medium text-gray-900">{getTypeDisplayName(paymentParams.type)}</span>
                </div>
                {paymentParams.type !== 'none' &&
                  paymentParams.amount &&
                  paymentParams.amount !== '0' &&
                  paymentParams.currency && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('Amount:')}</span>
                      <span className="font-medium text-gray-900">
                        {formatAmount(paymentParams.amount, paymentParams.currency)}
                      </span>
                    </div>
                  )}
              </div>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">{t('Important Notice')}</p>
                <p>
                  {t(
                    "You haven't completed your damage protection selection. You may still be responsible for any damages that occur during your stay."
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() =>
                navigate(
                  paymentParams.returnPath
                    ? `${damageWaiverPath}?return=${encodeURIComponent(paymentParams.returnPath)}`
                    : damageWaiverPath
                )
              }
              className="w-full"
              style={{
                backgroundColor: 'rgb(62, 104, 192)',
                borderColor: 'rgb(62, 104, 192)',
              }}
            >
              {t('Try Again')}
            </Button>
            <Button variant="outline" onClick={() => navigate(backPath)} className="w-full">
              {t('Skip for Now')}
            </Button>
          </div>
        </div>
      </div>
    </Shell>
  );
}

export default DamageWaiverResult;
